<script setup lang="ts">
import type { MainSectionPhoto, TripData } from '~/types/trip'
import type { PhotoModalOption } from '~/components/PhotoModal.vue'

import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

type PhotoAction = 'none' | 'generate'

const mainStore = useMainStore()
const authStore = useAuthStore()
const interactionStore = useInteractionStore()

const { t } = useI18n()

const { trackEvent } = useAnalyticsTrackingService()
const { likeTripPhoto, unlikeTripPhoto } = useLikeService()
const { getTrip, createAIStylizePhoto, deletePhoto } = useTripService()

const props = defineProps<{
  trip?: TripData
  tripId?: number | string
  selectedPhotoId?: number | string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:selectedPhotoId', photoId: number | string | undefined): void
}>()

const trip = ref<TripData | undefined>(props.trip)
const isLoadingTrip = ref(false)

watch(
  () => props.tripId,
  async (tripId) => {
    if (tripId && !props.trip) {
      isLoadingTrip.value = true
      try {
        trip.value = await getTrip(String(tripId))
      } catch (error) {
        console.error('Error fetching trip:', error)
      } finally {
        isLoadingTrip.value = false
      }
    }
  },
  { immediate: true }
)

watch(
  () => props.trip,
  (newTrip) => {
    if (newTrip) {
      trip.value = newTrip
    }
  },
  { immediate: true }
)

const sortedPhotos = computed(() => {
  if (!trip.value) return []

  const byTimestamp = (a: MainSectionPhoto, b: MainSectionPhoto) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()

  const photos = trip.value.main_section.photos
  const regularPhotos = photos.filter((photo) => !photo.ai).sort(byTimestamp)
  const aiPhotos = photos.filter((photo) => photo.ai).sort(byTimestamp)

  return [...regularPhotos, ...aiPhotos]
})

const state = reactive<{
  selectedItem?: number | string
  likeLoading: boolean
  deleteLoading: boolean
  showDeleteConfirm: boolean
  aiImage: {
    generatingPhotoId: number | null
    showConsentDialog: boolean
    pendingGeneratePhotoId: number | null
  }
}>({
  selectedItem: props.selectedPhotoId ?? (trip.value ? sortedPhotos.value[0]?.id : undefined),
  likeLoading: false,
  deleteLoading: false,
  showDeleteConfirm: false,
  aiImage: {
    generatingPhotoId: null,
    showConsentDialog: false,
    pendingGeneratePhotoId: null
  }
})

const user = computed(() => trip.value?.user ?? null)

const isOwner = computed(() => {
  if (!trip.value || !authStore.currentUser) return false

  return authStore.currentUser.id === trip.value.user_id
})

const distanceUnit = computed((): 'metric' | 'imperial' => {
  if (mainStore.settings?.distance === 'imperial') return 'imperial'

  return 'metric'
})

const isGenerating = computed(() => state.aiImage.generatingPhotoId !== null)

watch(
  () => props.selectedPhotoId,
  (newPhotoId) => {
    if (newPhotoId !== undefined) {
      state.selectedItem = newPhotoId
    }
  },
  { immediate: true }
)

const currentPhoto = computed(() => {
  if (!state.selectedItem) return

  return getPhotoById(state.selectedItem)
})

const currentPhotoIsLikedByMe = computed(() => {
  if (!currentPhoto.value) return false

  const storedTripPhoto = interactionStore.getItem('tripPhotos', currentPhoto.value.id)

  return storedTripPhoto == null ? currentPhoto.value.liked_by_me : storedTripPhoto.likedByMe
})

const currentPhotoLikeCount = computed(() => {
  if (!currentPhoto.value) return 0

  return (
    interactionStore.getItem('tripPhotos', currentPhoto.value.id)?.likeCount ??
    currentPhoto.value.likes_count
  )
})

const photoOptions = computed<PhotoModalOption[]>(() => {
  if (!isOwner.value || isGenerating.value) return []

  if (!currentPhoto.value) return []

  return [
    {
      key: 'delete',
      label: t('Delete'),
      fontAwesomeIcon: 'trash'
    }
  ]
})

const getPhotoById = (id: number | string): MainSectionPhoto | undefined => {
  if (!trip.value) return

  return trip.value.main_section.photos.find((p) => p.id === id)
}

const photoActionFor = (id: number | string): PhotoAction => {
  const photo = getPhotoById(id)

  if (!photo || !isOwner.value) return 'none'

  if (state.aiImage.generatingPhotoId !== null) return 'none'

  if (photo.ai) return 'none'

  return 'generate'
}

const appendPhoto = (newPhoto: MainSectionPhoto) => {
  if (!trip.value) return

  trip.value.main_section.photos.push(newPhoto)
}

const runGenerate = async (photoId: number) => {
  if (!trip.value) return

  state.aiImage.generatingPhotoId = photoId

  try {
    trackEvent(ANALYTICS_EVENTS.tripPhotoAiCreate(distanceUnit.value))

    const newPhoto = await createAIStylizePhoto(photoId, distanceUnit.value)

    if (!newPhoto?.large) {
      throw new Error('AI stylize returned photo without large URL')
    }

    appendPhoto(newPhoto)
    state.selectedItem = newPhoto.id
    emit('update:selectedPhotoId', newPhoto.id)
  } catch (error) {
    console.error('Error generating AI photo', error)
  } finally {
    state.aiImage.generatingPhotoId = null
    state.aiImage.pendingGeneratePhotoId = null
  }
}

const onGenerateClick = (id: number | string) => {
  const photo = getPhotoById(id)

  if (!photo) return

  const photoId = photo.id

  if (authStore.currentUser?.ai_image_consent) {
    runGenerate(photoId)
    return
  }

  state.aiImage.pendingGeneratePhotoId = photoId
  state.aiImage.showConsentDialog = true
}

const onConsentConfirmed = () => {
  state.aiImage.showConsentDialog = false

  const photoId = state.aiImage.pendingGeneratePhotoId

  if (photoId != null) {
    runGenerate(photoId)
  }
}

const onConsentClose = () => {
  state.aiImage.showConsentDialog = false
  state.aiImage.pendingGeneratePhotoId = null
}

const onOptionSelect = (key: string) => {
  if (key === 'delete') {
    state.showDeleteConfirm = true
  }
}

const deleteCurrentPhoto = async () => {
  if (!trip.value || !currentPhoto.value) return

  const photoId = currentPhoto.value.id
  const currentSortedIndex = sortedPhotos.value.findIndex((photo) => photo.id === photoId)

  state.deleteLoading = true

  try {
    await deletePhoto(String(photoId))

    const photos = trip.value.main_section.photos
    const rawIndex = photos.findIndex((photo) => photo.id === photoId)

    if (rawIndex !== -1) photos.splice(rawIndex, 1)

    state.showDeleteConfirm = false

    const remainingPhotos = sortedPhotos.value

    if (remainingPhotos.length === 0) {
      close()
      return
    }

    const nextIndex = Math.min(currentSortedIndex, remainingPhotos.length - 1)
    updateSelectedPhoto(remainingPhotos[nextIndex].id)
  } catch (error) {
    console.error('Error deleting photo', error)
  } finally {
    state.deleteLoading = false
  }
}

const togglePhotoLike = async () => {
  if (!currentPhoto.value || isGenerating.value) return

  state.likeLoading = true

  if (!interactionStore.hasItem('tripPhotos', currentPhoto.value.id)) {
    interactionStore.initItem('tripPhotos', currentPhoto.value.id, {
      likedByMe: currentPhoto.value.liked_by_me,
      likeCount: currentPhoto.value.likes_count,
      commentCount: 0
    })
  }

  try {
    if (currentPhotoIsLikedByMe.value) {
      await unlikeTripPhoto(currentPhoto.value.id)
    } else {
      trackEvent(ANALYTICS_EVENTS.tripPhotoLike())

      await likeTripPhoto(currentPhoto.value.id)
    }
  } catch (error) {
    console.error('Error toggling photo like', error)
  } finally {
    state.likeLoading = false
  }
}

const close = () => {
  emit('close')
}

const updateSelectedPhoto = (photoId: number | string | undefined) => {
  state.selectedItem = photoId
  emit('update:selectedPhotoId', photoId)
}

onMounted(() => {
  if (trip.value) mainStore.hideScrollbar()
})

onUnmounted(() => {
  if (trip.value) mainStore.unhideScrollbar()
})
</script>

<template>
  <PhotoModal
    v-if="trip && !isLoadingTrip"
    v-model="state.selectedItem"
    :photos="sortedPhotos.map((p) => ({ id: p.id, url: p.large }))"
    :navigation-disabled="isGenerating"
    :options="photoOptions"
    @close="close"
    @update:model-value="updateSelectedPhoto"
    @option-select="onOptionSelect"
  >
    <template #user-info>
      <PostingUserInfo
        :user="user ?? undefined"
        :creation-date="new Date(trip.date)"
        has-dark-background
        class="tw-text-white"
      />
    </template>

    <template #content-extra>
      <div
        v-if="isGenerating"
        class="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-black/70 tw-px-6 tw-text-center tw-text-white tw-backdrop-blur-sm"
      >
        <IconsAiSparkle animate class="tw-h-12 tw-w-12" />

        <p class="tw-text-lg tw-font-bold">
          {{ t('generate_ai_image') }}
        </p>

        <p class="tw-text-sm tw-text-white/80">
          {{ t('ai_image_can_take_upto_a_minute') }}
        </p>
      </div>
    </template>

    <template #item-action="{ identifier }">
      <InputAppButton
        v-if="photoActionFor(identifier) === 'generate'"
        class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-gradient-to-r tw-from-riser-blue tw-via-riser-green tw-to-riser-yellow tw-px-6 tw-py-2.5 tw-font-bold tw-text-white"
        @click="onGenerateClick(identifier)"
      >
        <IconsAiSparkle variant="white" class="tw-h-5 tw-w-5 tw-shrink-0" />
        {{ t('ai_image_banner_title') }}
      </InputAppButton>
    </template>

    <template #bottom-bar>
      <div class="tw-w-full tw-bg-black/10 tw-px-4 tw-py-4 tw-text-white">
        <div class="tw-mx-auto tw-flex tw-w-full tw-max-w-4xl tw-justify-between">
          <p>
            <span class="tw-font-bold">{{ currentPhotoLikeCount }}</span>
            <span>{{ t('x_likes', currentPhotoLikeCount) }}</span>
          </p>

          <AppLikeButton
            v-if="!isGenerating"
            :is-liked="currentPhotoIsLikedByMe"
            :loading="state.likeLoading"
            inactive-color-class="tw-text-white/40"
            @click="togglePhotoLike"
          />
        </div>
      </div>
    </template>
  </PhotoModal>

  <AiImageConsentDialog
    v-if="state.aiImage.showConsentDialog"
    @confirmed="onConsentConfirmed"
    @close="onConsentClose"
  />

  <ConfirmDialog
    v-if="state.showDeleteConfirm"
    :title="t('Are you sure you want to delete this photo?')"
    :options="[
      {
        text: t('Yes'),
        onClick: deleteCurrentPhoto,
        class: 'tw-bg-riser-red tw-text-white tw-py-2 tw-rounded-lg'
      },
      {
        text: t('No'),
        onClick: () => (state.showDeleteConfirm = false),
        class: 'tw-bg-gray-200 tw-py-2 tw-rounded-lg'
      }
    ]"
    @close="state.showDeleteConfirm = false"
  />
</template>
